'use client';

import classNames from 'classnames';
import { useTranslation } from 'react-i18next';
import { useMemo } from 'react';

import Link from 'next/link';

import { PropsWithClassname } from '@/app/types';
import { formatPriceInCents } from '@/app/utils/formatting';
import { ProjectPhoto } from '@/app/api';

import ProgressBar from '@/app/components/ProgressBar/ProgressBar';

interface SearchResultProjectCardProps extends PropsWithClassname {
  title: string;
  href: string;
  image?: ProjectPhoto;
  currentAmountInCents: number;
  targetAmountInCents?: number;
  isProjectActive: boolean;
  onNavigate?: () => void;
}

const SearchResultProjectCard = ({
  title,
  href,
  image,
  currentAmountInCents,
  targetAmountInCents,
  isProjectActive,
  onNavigate,
  className,
}: SearchResultProjectCardProps) => {
  const { t } = useTranslation();

  const percentageAchieved = useMemo(() => {
    if (!targetAmountInCents) {
      return;
    }

    return Math.floor(Math.min(currentAmountInCents / (targetAmountInCents / 100), 100));
  }, [targetAmountInCents, currentAmountInCents]);

  return (
    <Link
      href={href}
      onClick={onNavigate}
      prefetch={process.env.NEXT_PUBLIC_PREFETCH_LINKS === 'true'}
      className={classNames(
        className,
        'group flex w-full h-[102px] lg:h-[132px] grow-0 shrink-0 bg-white rounded border border-neutral-light shadow-md hover:shadow-xl overflow-hidden transition-all duration-500',
      )}
    >
      {!!image && (
        <span className="block w-[102px] shrink-0 grow-0 lg:w-[132px] overflow-hidden">
          <img
            width={0}
            height={0}
            src={image.sizes.smallCropped.path}
            alt={image.description ?? title}
            sizes="102px, (min-width: 1024px) 132px"
            className="relative left-[50%] translate-x-[-50%] w-auto max-w-none h-full scale-1 group-hover:scale-110 transition-all linear duration-[3000ms]"
          />
        </span>
      )}
      <span className="flex flex-col justify-between p-[8px] lg:p-[18px] grow">
        <h3 className="mb-[8px] text-[16px] leading-[18px] lg:text-[20px] leading-[24px] font-medium text-balance line-clamp-2 ellipsis">
          {title}
        </h3>
        {isProjectActive && (
          <span className="block max-w-[264px]">
            <ProgressBar
              total={targetAmountInCents ?? currentAmountInCents}
              currentValue={currentAmountInCents}
              className="mb-[8px]"
              indicateFinishedWithPrimaryColor={!targetAmountInCents}
            />
            <span className="flex justify-between">
              <span className="block font-medium break-words text-[12px] lg:text-[16px]">
                {t('components.SearchResultProjectCard.collected', {
                  amount: formatPriceInCents(currentAmountInCents),
                })}
              </span>
              {!!targetAmountInCents && (
                <span className="text-primary-mid block font-medium break-words text-[12px] lg:text-[16px]">
                  {percentageAchieved} %
                </span>
              )}
            </span>
          </span>
        )}
        {!isProjectActive && (
          <span className="text-secondary-mid text-[10px] tracking-[3px] leading-[24px] lg:text-[12px] lg:leading-[24px] lg:tracking-[4.5px] font-medium uppercase">
            {t('components.SearchResultProjectCard.projectFinished')}
          </span>
        )}
      </span>
    </Link>
  );
};

export default SearchResultProjectCard;
